/**
 * PaymentMandateSelector Component
 *
 * Allows selecting an existing payment mandate for a client.
 * Used in RecurringInvoiceForm to link auto-payment.
 */

import React from 'react';
import {
  Building2,
  CreditCard,
  FileText,
  Check,
  AlertCircle,
  Plus,
  Loader2,
} from 'lucide-react';
import { useClientMandates } from '../hooks/usePaymentMandates';
import type { PaymentMandate, MandateProvider } from '../types/mandate.types';

interface PaymentMandateSelectorProps {
  clientId: string;
  value: string | null;
  onChange: (mandateId: string | null) => void;
  onCreateNew?: () => void;
  showCreateButton?: boolean;
}

const providerIcons: Record<MandateProvider, React.ReactNode> = {
  GOCARDLESS: <Building2 className="w-5 h-5" />,
  STRIPE: <CreditCard className="w-5 h-5" />,
  MANUAL: <FileText className="w-5 h-5" />,
};

const providerLabels: Record<MandateProvider, string> = {
  GOCARDLESS: 'SEPA Direct Debit',
  STRIPE: 'Carte bancaire',
  MANUAL: 'Paiement manuel',
};

const statusLabels: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-700' },
  PENDING_SUBMISSION: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700' },
  PENDING_CUSTOMER_APPROVAL: { label: 'En attente client', color: 'bg-yellow-100 text-yellow-700' },
  SUBMITTED: { label: 'Soumis', color: 'bg-blue-100 text-blue-700' },
  FAILED: { label: 'Echoue', color: 'bg-red-100 text-red-700' },
  CANCELLED: { label: 'Annule', color: 'bg-gray-100 text-gray-700' },
  EXPIRED: { label: 'Expire', color: 'bg-gray-100 text-gray-700' },
};

function formatMandateDetails(mandate: PaymentMandate): string {
  if (mandate.ibanLast4) {
    return `IBAN ****${mandate.ibanLast4}`;
  }
  if (mandate.cardLast4) {
    return `${mandate.cardBrand || 'Carte'} ****${mandate.cardLast4}`;
  }
  if (mandate.accountHolderName) {
    return mandate.accountHolderName;
  }
  return 'Mandat configure';
}

export function PaymentMandateSelector({
  clientId,
  value,
  onChange,
  onCreateNew,
  showCreateButton = true,
}: PaymentMandateSelectorProps) {
  const { data: mandates, isLoading, error } = useClientMandates(clientId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-red-600">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm">Erreur lors du chargement des mandats</p>
      </div>
    );
  }

  const activeMandates = mandates?.filter((m) => m.status === 'ACTIVE') || [];

  if (activeMandates.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-4 rounded-xl bg-gray-50 text-gray-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p className="text-sm">Aucun mandat de paiement actif pour ce client</p>
        </div>

        {showCreateButton && onCreateNew && (
          <button
            type="button"
            onClick={onCreateNew}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Configurer un mode de paiement
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* No selection option */}
      <button
        type="button"
        onClick={() => onChange(null)}
        className={`
          w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all
          ${
            !value
              ? 'border-teal-600 bg-teal-50/50'
              : 'border-gray-200 hover:border-gray-300'
          }
        `}
      >
        <div
          className={`p-2 rounded-lg ${!value ? 'bg-teal-100' : 'bg-gray-100'}`}
        >
          <FileText
            className={`w-5 h-5 ${!value ? 'text-teal-600' : 'text-gray-500'}`}
          />
        </div>
        <div className="flex-1 text-left">
          <p className={`font-medium ${!value ? 'text-teal-700' : 'text-gray-700'}`}>
            Pas de paiement automatique
          </p>
          <p className="text-sm text-gray-500">Factures envoyees sans prelevement</p>
        </div>
        {!value && (
          <div className="p-1 bg-teal-600 rounded-full">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
      </button>

      {/* Active mandates */}
      {activeMandates.map((mandate) => {
        const isSelected = value === mandate.id;
        const status = statusLabels[mandate.status] || {
          label: mandate.status,
          color: 'bg-gray-100 text-gray-700',
        };

        return (
          <button
            key={mandate.id}
            type="button"
            onClick={() => onChange(mandate.id)}
            className={`
              w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all
              ${
                isSelected
                  ? 'border-teal-600 bg-teal-50/50'
                  : 'border-gray-200 hover:border-gray-300'
              }
            `}
          >
            <div
              className={`p-2 rounded-lg ${
                isSelected ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {providerIcons[mandate.provider]}
            </div>

            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <p
                  className={`font-medium ${
                    isSelected ? 'text-teal-700' : 'text-gray-700'
                  }`}
                >
                  {providerLabels[mandate.provider]}
                </p>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}
                >
                  {status.label}
                </span>
              </div>
              <p className="text-sm text-gray-500">{formatMandateDetails(mandate)}</p>
            </div>

            {isSelected && (
              <div className="p-1 bg-teal-600 rounded-full">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
          </button>
        );
      })}

      {/* Create new option */}
      {showCreateButton && onCreateNew && (
        <button
          type="button"
          onClick={onCreateNew}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-dashed border-gray-300 text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Ajouter un nouveau mode de paiement
        </button>
      )}
    </div>
  );
}

export default PaymentMandateSelector;
